import React from "react";
import { motion } from "framer-motion";
import cvData from "../data/cvData";
import "../components/Section.css";
import Seo from "../components/Seo";
import { fadeUp, staggerContainer, staggerItem, viewportOnce, easing } from "../utils/motionVariants";
import "./ExperiencePage.css";

export default function ExperiencePage() {
  const { experience, education, thesis, certifications, languages } = cvData;
  const latest = experience[0];
  const seoDescription = latest
    ? `Experience timeline for ${cvData.name} - currently ${latest.role} at ${latest.company}. ${experience.length}+ professional roles across full-stack web, mobile, and AI engineering.`
    : `Professional experience timeline for ${cvData.name}.`;

  return (
    <section id="experience" className="section section-peach experience-page">
      <Seo
        title="Experience"
        path="/experience"
        description={seoDescription}
      />
      <motion.div
        className="experience-hero"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={staggerItem}>
          <p className="eyebrow">Experience</p>
          <h2>Delivering thoughtful products across teams.</h2>
          <p>
            I partner with founders and engineering leads to design, ship, and iterate on digital experiences.
            From rapid prototyping to production-ready releases, I stay close to user outcomes and business KPIs.
          </p>
        </motion.div>
        <motion.div
          className="experience-stats"
          variants={staggerContainer}
        >
          {[
            { value: `${experience.length}+`, label: "Professional Roles" },
            { value: certifications.length, label: "Certifications" },
            { value: languages.length, label: "Languages" },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              variants={staggerItem}
              whileHover={{ y: -4, scale: 1.04 }}
              transition={{ duration: 0.25, ease: easing }}
            >
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        className="experience-list"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        {experience.map((role) => (
          <motion.article
            key={role.company}
            className="experience-card"
            variants={staggerItem}
            whileHover={{ x: 6, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)" }}
            transition={{ duration: 0.25, ease: easing }}
          >
            <div className="experience-company">
              {role.logo && (
                <motion.img
                  src={role.logo}
                  alt={`${role.company} logo`}
                  className="experience-company-logo"
                  loading="lazy"
                  whileHover={{ rotate: -6, scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
              <p className="experience-company-name">{role.company}</p>
              <p className="muted experience-location">{role.location}</p>
            </div>
            <div className="experience-card-head">
              <h3>{role.role}</h3>
              <span className="experience-period">{role.period}</span>
            </div>
            <p>{role.details}</p>
          </motion.article>
        ))}
      </motion.div>

      <motion.div
        className="experience-grid"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <motion.div
          className="education-card"
          variants={fadeUp}
          whileHover={{ y: -6, boxShadow: "0 20px 45px rgba(15, 23, 42, 0.12)" }}
          transition={{ duration: 0.3, ease: easing }}
        >
          <p className="eyebrow">Education</p>
          <h3>{education.degree}</h3>
          <p className="muted">{education.institution}</p>
          <p>{education.other}</p>
          <div className="thesis-mini">
            <span>Thesis</span>
            <strong>{thesis.title}</strong>
            <p className="muted">{thesis.completed}</p>
          </div>
        </motion.div>

        <motion.div
          className="cert-panel"
          variants={fadeUp}
          whileHover={{ y: -6, boxShadow: "0 20px 45px rgba(15, 23, 42, 0.12)" }}
          transition={{ duration: 0.3, ease: easing }}
        >
          <div>
            <p className="eyebrow">Certifications</p>
            <motion.ul
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              {certifications.map((cert) => (
                <motion.li
                  key={cert}
                  variants={staggerItem}
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  {cert}
                </motion.li>
              ))}
            </motion.ul>
          </div>
          <div>
            <p className="eyebrow">Languages</p>
            <p>{languages.join(" • ")}</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
